import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientSettings } from '../../contexts/ClientSettingsContext';
import WebRTCDialer from '../../components/dialer/WebRTCDialer';
import { Phone, Lock, AlertTriangle } from 'lucide-react';

const DialerPage = () => {
  const { clientUser } = useAuth();
  const { hasFeature, loading } = useClientSettings();

  // Check if outbound calls feature is enabled
  const canMakeCalls = hasFeature('outbound_calls');
  const canReceiveCalls = hasFeature('inbound_calls');
  const isDialerEnabled = canMakeCalls || canReceiveCalls;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If dialer is completely disabled
  if (!isDialerEnabled) {
    return (
      <div className="space-y-6" data-testid="dialer-page">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Dialer</h1>
          <p className="text-gray-400">Make and receive calls using the built-in browser dialer</p>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
          <Lock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-yellow-400 mb-2">Dialer Disabled</h2>
          <p className="text-gray-400">
            The dialer feature is not enabled for your account. Please contact your administrator to enable calling features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dialer-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Dialer</h1>
        <p className="text-gray-400">Make and receive calls using the built-in browser dialer</p>
      </div>

      {/* Feature Status Banners */}
      {!canMakeCalls && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400" />
          <span className="text-yellow-400 text-sm">Outbound calls are disabled by admin</span>
        </div>
      )}
      {!canReceiveCalls && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400" />
          <span className="text-yellow-400 text-sm">Inbound calls are disabled by admin</span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Dialer Widget */}
        <div className="lg:w-auto">
          <WebRTCDialer
            userId={clientUser?.id}
            userType="client"
            showAIAssist={true}
            onCallStart={(call) => console.log('Call started:', call)}
            onCallEnd={(data) => console.log('Call ended:', data)}
          />
        </div>

        {/* Instructions Panel */}
        <div className="flex-1 space-y-6">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">How to Use the Dialer</h2>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-400/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-400 font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-white font-medium">Select a phone number</h3>
                  <p className="text-gray-400 text-sm">Choose which number to call from using the dropdown</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-400/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-400 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-white font-medium">Enter the destination number</h3>
                  <p className="text-gray-400 text-sm">Type the phone number using the dial pad or paste it directly</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-400/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-400 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-white font-medium">Click Call</h3>
                  <p className="text-gray-400 text-sm">The call will connect through your browser</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-400/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-400 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-white font-medium">Use call controls</h3>
                  <p className="text-gray-400 text-sm">Mute, hold, transfer calls, or use the keypad during the call</p>
                </div>
              </div>
            </div>
          </div>

          {/* Features Panel */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Dialer Features</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-400 mt-2"></div>
                <div>
                  <h3 className="text-white text-sm font-medium">Browser-based calling</h3>
                  <p className="text-gray-500 text-xs">No software installation needed</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-400 mt-2"></div>
                <div>
                  <h3 className="text-white text-sm font-medium">HD Voice Quality</h3>
                  <p className="text-gray-500 text-xs">Crystal clear audio with OPUS codec</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-400 mt-2"></div>
                <div>
                  <h3 className="text-white text-sm font-medium">Call Controls</h3>
                  <p className="text-gray-500 text-xs">Mute, hold, transfer, and DTMF</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-400 mt-2"></div>
                <div>
                  <h3 className="text-white text-sm font-medium">AI Assist</h3>
                  <p className="text-gray-500 text-xs">Real-time transcription and suggestions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h3 className="text-blue-400 font-medium mb-2">Requirements</h3>
            <ul className="text-gray-400 text-sm space-y-1">
              <li>• Modern browser (Chrome, Firefox, Edge, Safari)</li>
              <li>• Microphone access permission</li>
              <li>• Stable internet connection</li>
              <li>• Speakers or headphones</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialerPage;
